import { Input } from 'components/Forms'
import { IProps as InputProps } from 'components/Forms/Input'
import { PathsEnum } from 'pages/Paths'
import React, { useEffect, useRef, useState } from 'react'
import { FaKey } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'

import { PasswordSetup } from './PasswordSetup'

interface IProps {
  title: string
  passwordLabel: string
  newPassword: string
  passwordResetCode: string
  handleChangePassword: (oldPasswordOrResetCode: string, newPassword: string) => void
  handleCodeResend: () => void
  setNewPassword: (value: string) => void
  setPasswordResetCode: (value: string) => void

  showResendCodeButton?: boolean
  showOptionalButton?: boolean
  message?: JSX.Element
  className?: string
  isInputTypePassword?: boolean
  isLoadingChangePassword?: boolean
  isLoadingResendCode?: boolean
  showExtraRequirement?: boolean
}

export const AccountResetPassword = ({
  className,
  message,
  title,
  passwordLabel,
  newPassword,
  passwordResetCode,
  setNewPassword,
  setPasswordResetCode,
  handleCodeResend,
  handleChangePassword,
  showResendCodeButton = true,
  showOptionalButton = true,
  isInputTypePassword = false,
  // isLoadingChangePassword = false,
  isLoadingResendCode = false,
  showExtraRequirement = false,
}: IProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [isChangePasswordEnabled, setIsChangePasswordEnabled] = useState(false)
  const navigate = useNavigate()
  const inputFieldProps: Partial<InputProps> = {
    name: 'password-reset-code-input',
    label: passwordLabel,
    value: passwordResetCode,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setPasswordResetCode(e.target.value),
    ...(isInputTypePassword ? { type: 'password' } : {}),
    ...(isInputTypePassword ? { isPasswordWithReveal: true } : {}),
  }

  const goToLogin = () => {
    navigate(PathsEnum.root)
    window.location.reload()
  }

  useEffect(() => {
    setIsChangePasswordEnabled(isPasswordValid && !!passwordResetCode)
  }, [isPasswordValid, passwordResetCode])
  return (
    <Section className={`reset-password-container ${className}`}>
      <header className="title">{title}</header>
      {!!message && <section className="message-container">{message}</section>}
      {showResendCodeButton && (
        <section className="resend-code-button-container">
          <Button
            variant="info"
            size="large"
            radius="rounded"
            width="100%"
            onClick={handleCodeResend}
            disabled={isLoadingResendCode}
          >
            Resend code
          </Button>
        </section>
      )}
      <section className="input-container">
        <Input {...(inputFieldProps as InputProps)} ref={inputRef} autoComplete="new-password" />
      </section>
      <section className="divider-container">
        <div></div>
        <FaKey />
        <div></div>
      </section>
      <PasswordSetup
        showExtraRequirement={showExtraRequirement}
        oldPassword={passwordResetCode}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        setIsValidForNextStep={setIsPasswordValid}
      />
      <section className="actions-container">
        <Button
          width="100%"
          variant="primary"
          radius="rounded"
          size="large"
          disabled={!isChangePasswordEnabled}
          onClick={() => handleChangePassword(inputRef?.current?.value || '', newPassword)}
        >
          Change Password
        </Button>
      </section>

      {showOptionalButton && (
        <p className="text-center d-block mt-3">
          Return to{' '}
          <a className="text-underline" onClick={() => goToLogin()}>
            Login Page
          </a>
        </p>
      )}
    </Section>
  )
}

const Section = styled.section`
  header.title {
    margin: 0.5rem 0 0.5rem 0;
    color: ${({ theme }) => theme.colors.primary.darker};
    ${({ theme }) => theme.typography.h4.normal};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }

  .text-underline {
    text-decoration: underline;
    cursor: pointer;
  }

  .message-container {
    margin-bottom: 1.25rem;
    p {
      ${({ theme }) => theme.typography.xs.normal};
      color: ${({ theme }) => theme.colors.grey.darker};
      font-weight: ${({ theme }) => theme.fontWeight.normal};

      .email {
        font-weight: ${({ theme }) => theme.fontWeight.bold};
      }
    }
  }

  .resend-code-button-container {
    margin-bottom: 1.25rem;
    button {
      ${({ theme }) => theme.typography.s.normal};
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      text-decoration: underline;
      text-decoration-color: ${({ theme }) => theme.colors.info.darker};
    }
  }

  .input-container {
    margin-bottom: 1.5rem;
    input {
      outline: 1px solid ${({ theme }) => theme.colors.primary.lighter};
      text-align: center;
    }
    label > div {
      color: ${({ theme }) => theme.colors.grey.darker};
      font-weight: ${({ theme }) => theme.fontWeight.bold};
    }
  }

  .divider-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    & div:first-child {
      height: 2px;
      width: 100%;
      background-color: ${({ theme }) => theme.colors.primary.lightest};
    }

    & svg {
      display: inline-block;
      width: 0.875rem;
      height: 0.875rem;
      flex-shrink: 0;
      color: ${({ theme }) => theme.colors.primary.lighter};
    }

    & div:last-child {
      height: 2px;
      width: 100%;
      background-color: ${({ theme }) => theme.colors.primary.lightest};
    }
  }

  .actions-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;
  }
`
